/**
 * Sticky Bar Component
 * Handles sticky bar display, price updates, and buy button functionality
 */
class StickyBar {
  constructor(container) {
    this.container = container;
    this.productData = this.getProductData();
    this.isVisible = false;
    this.scrollThreshold = 200; // Show sticky bar after scrolling 200px
    this.footer =
      document.querySelector("footer") || document.querySelector(".footer");

    // 定义移动端断点 (例如 768px 或 990px)
    this.mobileBreakpoint = 768;

    this.init();
  }

  init() {
    this.bindEvents();
    this.updateVisibility();
  }

  /**
   * Get product data from the JSON script tag
   */
  getProductData() {
    const scriptTag = this.container.querySelector("[data-product-data]");
    if (!scriptTag) {
      console.error("Sticky bar product data not found");
      return null;
    }

    try {
      const data = JSON.parse(scriptTag.textContent);
      return data;
    } catch (error) {
      console.error("Failed to parse sticky bar product data:", error);
      return null;
    }
  }

  /**
   * Bind event listeners
   */
  bindEvents() {
    // 1. Scroll event to show/hide sticky bar
    window.addEventListener(
      "scroll",
      this.throttle(() => {
        this.updateVisibility();
      }, 100),
    );

    // 2. Resize event: 监听窗口大小变化，从 Mobile 变到 PC 时需要立即隐藏
    window.addEventListener(
      "resize",
      this.throttle(() => {
        this.updateVisibility();
      }, 100),
    );

    // Listen for price updates from main product component
    document.addEventListener("productPriceUpdated", (event) => {
      this.updatePrice(event.detail);
    });

    // Buy button click
    this.container.addEventListener("click", (e) => {
      if (e.target.closest('[data-action="preorder"]')) {
        this.handleBuyClick();
      }
    });
  }

  /**
   * Update sticky bar visibility based on scroll position
   * Modified: Only show on mobile devices
   */
  updateVisibility() {
    // --- 新增逻辑：PC端 (宽度 > 768px) 强制不显示 ---
    if (window.innerWidth > this.mobileBreakpoint) {
      if (this.isVisible) {
        this.hide();
      }
      return; // 直接结束，不执行后续逻辑
    }
    // ------------------------------------------------

    const scrollY = window.scrollY;
    const windowHeight = window.innerHeight;

    // 1. 判断显示条件：滚动超过阈值
    const isScrolledPastThreshold = scrollY > this.scrollThreshold;

    // 2. 判断隐藏条件 (互斥条件)

    // 检查 order-summary 是否在视口内
    let orderSummaryVisible = false;
    const orderSummary = document.querySelector(".order-summary");
    if (orderSummary) {
      const orderRect = orderSummary.getBoundingClientRect();
      orderSummaryVisible =
        orderRect.top < windowHeight && orderRect.bottom > 0;
    }

    // 检查 footer 是否在视口内
    let footerVisible = false;
    if (this.footer) {
      const footerRect = this.footer.getBoundingClientRect();
      footerVisible = footerRect.top < windowHeight;
    }

    // 3. 最终决定
    // 必须满足：滚动超过阈值 且 没看到订单摘要 且 没看到页脚
    const shouldShow =
      isScrolledPastThreshold && !orderSummaryVisible && !footerVisible;

    if (shouldShow && !this.isVisible) {
      this.show();
    } else if (!shouldShow && this.isVisible) {
      this.hide();
    }
  }

  /**
   * Update visibility for product pages
   * (保留此方法以防外部调用，但也加入PC端检查)
   */
  updateProductPageVisibility(functionBundle, orderSummary) {
    // PC端强制隐藏
    if (window.innerWidth > this.mobileBreakpoint) {
      if (this.isVisible) this.hide();
      return;
    }

    // Check if function-bundle is within 300px of the top
    const bundleRect = functionBundle.getBoundingClientRect();
    const functionBundleReady = bundleRect.top <= 300;

    // Check if order-summary is visible
    let orderSummaryVisible = false;
    if (orderSummary) {
      const orderRect = orderSummary.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      orderSummaryVisible =
        orderRect.top < windowHeight && orderRect.bottom > 0;
    }

    // Check if footer is visible
    let footerVisible = false;
    if (this.footer) {
      const footerRect = this.footer.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      footerVisible = footerRect.top < windowHeight;
    }

    const shouldShow =
      functionBundleReady && !orderSummaryVisible && !footerVisible;

    if (shouldShow && !this.isVisible) {
      this.show();
    } else if (!shouldShow && this.isVisible) {
      this.hide();
    }
  }

  /**
   * Update visibility for non-product pages
   * (保留此方法以防外部调用，但也加入PC端检查)
   */
  updateNonProductPageVisibility() {
    // PC端强制隐藏
    if (window.innerWidth > this.mobileBreakpoint) {
      if (this.isVisible) this.hide();
      return;
    }

    const scrollY = window.scrollY;
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;

    // Check if we're near the footer
    let nearFooter = false;
    if (this.footer) {
      const footerRect = this.footer.getBoundingClientRect();

      // Hide sticky bar when footer comes into view
      nearFooter = footerRect.top < windowHeight;
    } else {
      // Fallback
      nearFooter = scrollY + windowHeight >= documentHeight - 100;
    }

    const shouldShow = scrollY > this.scrollThreshold && !nearFooter;

    if (shouldShow && !this.isVisible) {
      this.show();
    } else if (!shouldShow && this.isVisible) {
      this.hide();
    }
  }

  /**
   * Show sticky bar
   */
  show() {
    const isSectionStickyBar = this.container.closest(
      ".sticky-bar-section--always-visible",
    );

    if (isSectionStickyBar) {
      this.container.classList.remove("sticky-bar-section--hidden");
      this.container.classList.add("sticky-bar-section--visible");
    } else {
      this.container.classList.remove("hidden");
      this.container.classList.add("visible");
    }
    this.isVisible = true;
  }

  /**
   * Hide sticky bar
   */
  hide() {
    const isSectionStickyBar = this.container.closest(
      ".sticky-bar-section--always-visible",
    );

    if (isSectionStickyBar) {
      this.container.classList.remove("sticky-bar-section--visible");
      this.container.classList.add("sticky-bar-section--hidden");
    } else {
      this.container.classList.remove("visible");
      this.container.classList.add("hidden");
    }
    this.isVisible = false;
  }

  /**
   * Update price display
   */
  updatePrice(priceData) {
    const priceElement = this.container.querySelector("[data-total-price]");
    if (priceElement && priceData) {
      priceElement.textContent = priceData.formattedPrice;
      priceElement.dataset.totalPrice = priceData.formattedPrice;
    }

    // 比较折扣价与现价是否相同，判断是否显示折扣相关元素
    const totalPrice = priceData.totalPrice || 0;
    const totalCompareAtPrice = priceData.totalCompareAtPrice || 0;
    const pricesAreEqual =
      totalCompareAtPrice > 0 && totalCompareAtPrice === totalPrice;
    const shouldShowDiscount =
      totalCompareAtPrice > totalPrice && !pricesAreEqual;

    const isPriceActivityType =
      this.productData?.activity_type === "price" ||
      priceData.activity_type === "price";
    const isTieredPercentActivityType =
      this.productData?.activity_type === "tiered_percent" ||
      priceData.activity_type === "tiered_percent";

    // Always update save amount if it's a price or tiered_percent activity type
    if (
      (isPriceActivityType || isTieredPercentActivityType) &&
      priceData.saveAmount !== undefined
    ) {
      const formattedSave = this.formatPrice(priceData.saveAmount);

      const saveElement = this.container.querySelector("[data-save-amount]");
      const saveMobileElement = this.container.querySelector(
        ".sticky-bar-price-row--discount-text",
      );
      if (saveElement) {
        saveElement.textContent = `save ${formattedSave}`;
      }

      if (!shouldShowDiscount || priceData.saveAmount === 0) {
        if (saveElement) saveElement.classList.add("hidden");
        if (saveMobileElement) saveMobileElement.classList.add("hidden");
      } else {
        if (saveElement) saveElement.classList.remove("hidden");
        if (saveMobileElement) saveMobileElement.classList.remove("hidden");
      }

      const saveElementTop = this.container.querySelector(
        "[data-save-amount-top]",
      );
      if (saveElementTop) {
        saveElementTop.textContent = `SAVE ${formattedSave}`;
        if (!shouldShowDiscount || priceData.saveAmount === 0) {
          saveElementTop.parentElement?.classList.add("hidden");
        } else {
          saveElementTop.parentElement?.classList.remove("hidden");
        }
      }
    }

    // Update discount if available
    if (
      priceData.discountPercentage !== undefined &&
      priceData.discountPercentage !== null
    ) {
      const discountElement = this.container.querySelector(
        ".sticky-bar-discount",
      );
      if (discountElement) {
        discountElement.textContent = `-${priceData.discountPercentage}%`;
        if (!shouldShowDiscount) {
          discountElement.classList.add("hidden");
        } else {
          discountElement.classList.remove("hidden");
        }
      }
    }

    if (priceData.formattedCompareAtPrice) {
      const originalPriceElements = this.container.querySelectorAll(
        ".sticky-bar-original-price-amount",
      );
      const pcOriginalPriceElements = this.container.querySelectorAll(
        ".sticky-bar-original-price.text-show-on-pc .line-through",
      );

      // 只要满足 shouldShowDiscount (即 原价 > 现价)，就应该显示原价划线
      if (!shouldShowDiscount) {
        originalPriceElements.forEach((originalPriceElement) => {
          originalPriceElement.parentElement.classList.add("hidden");
        });
      } else {
        originalPriceElements.forEach((originalPriceElement) => {
          originalPriceElement.parentElement.classList.remove("hidden");
        });
      }

      const pcOriginalPriceContainers = this.container.querySelectorAll(
        ".sticky-bar-original-price.text-show-on-pc",
      );
      if (!shouldShowDiscount) {
        pcOriginalPriceContainers.forEach((container) => {
          container.classList.add("hidden");
        });
      } else {
        pcOriginalPriceContainers.forEach((container) => {
          container.classList.remove("hidden");
        });
      }

      if (originalPriceElements.length > 0) {
        originalPriceElements.forEach((originalPriceElement) => {
          originalPriceElement.textContent = priceData.formattedCompareAtPrice;
        });
      }

      if (pcOriginalPriceElements.length > 0) {
        pcOriginalPriceElements.forEach((pcElement) => {
          pcElement.textContent = priceData.formattedCompareAtPrice;
        });
      }
    }
  }

  /**
   * Format price for display
   */
  formatPrice(price) {
    if (isNaN(price) || !isFinite(price)) {
      return "$0.00";
    }

    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(price);
  }

  /**
   * Handle buy button click
   */
  handleBuyClick() {
    const buyEvent = new CustomEvent("stickyBarBuyClick", {
      detail: {
        productId: this.productData?.id,
      },
    });
    document.dispatchEvent(buyEvent);
  }

  /**
   * Throttle function to limit scroll event frequency
   */
  throttle(func, limit) {
    let inThrottle;
    return function () {
      const args = arguments;
      const context = this;
      if (!inThrottle) {
        func.apply(context, args);
        inThrottle = true;
        setTimeout(() => (inThrottle = false), limit);
      }
    };
  }
}

// Initialize sticky bar when DOM is ready
document.addEventListener("DOMContentLoaded", () => {
  const sectionStickyBars = document.querySelectorAll(
    ".sticky-bar-section--always-visible .sticky-bar",
  );
  sectionStickyBars.forEach((stickyBar) => {
    new StickyBar(stickyBar);
  });

  const snippetStickyBars = document.querySelectorAll(
    ".sticky-bar:not(.sticky-bar-section--always-visible .sticky-bar)",
  );
  snippetStickyBars.forEach((stickyBar) => {
    new StickyBar(stickyBar);
  });
});
